/**
 * Handle focus management when page loads with a hash anchor
 * This ensures screenreaders are moved to the target location after navigation
 *
 * For accessibility, target elements should have tabindex="-1" to be focusable
 *
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, details, [tabindex]:not([tabindex="-1"])';

function isNaturallyFocusable($el) {
  return $el.matches(FOCUSABLE_SELECTOR);
}

function hasMeaningfulContent($el) {
  return $el.textContent.trim().length > 0 || $el.children.length > 0;
}

function getNextMeaningfulElementSibling($el) {
  let $sibling = $el.nextElementSibling;

  while ($sibling) {
    if (hasMeaningfulContent($sibling)) {
      return $sibling;
    }
    $sibling = $sibling.nextElementSibling;
  }

  return null;
}

function resolveFocusTarget($target) {
  if (hasMeaningfulContent($target)) {
    return $target;
  }

  // Typo3 jumplinks are often empty anchors placed right before the actual content.
  if ($target.tagName === 'A') {
    const $fallbackTarget = getNextMeaningfulElementSibling($target);
    if ($fallbackTarget) {
      return $fallbackTarget;
    }
  }

  return null;
}

export function handleHashFocus() {
  const rawHash = window.location.hash.slice(1);

  if (!rawHash) {
    return;
  }

  const hash = decodeURIComponent(rawHash);
  const $hashTarget = document.getElementById(hash);
  const $target = $hashTarget ? resolveFocusTarget($hashTarget) : null;

  if (!$target) {
    return;
  }

  // Small delay to ensure page has scrolled and content is rendered.
  setTimeout(() => {
    $target.scrollIntoView({
      block: 'start',
      inline: 'nearest',
      behavior: 'auto',
    });

    const hadTabindex = $target.hasAttribute('tabindex');
    const needsTemporaryTabindex =
      !hadTabindex && !isNaturallyFocusable($target);

    if (needsTemporaryTabindex) {
      $target.setAttribute('tabindex', '-1');
      $target.setAttribute('data-hash-focus-temporary', 'true');
    }

    try {
      $target.focus({ preventScroll: true });
    } catch (error) {
      $target.focus();
    }

    if (!needsTemporaryTabindex) {
      $target.classList.add('focus-visible');
    }

    if (needsTemporaryTabindex) {
      const cleanupTemporaryTabindex = () => {
        $target.removeAttribute('data-hash-focus-temporary');
        $target.removeAttribute('tabindex');
      };

      $target.addEventListener('blur', cleanupTemporaryTabindex, {
        once: true,
      });
      setTimeout(cleanupTemporaryTabindex, 2000);
    }
  }, 100);
}

/**
 * Initialize hash focus handling on page load and hash changes.
 * Automatically called when module is imported
 */
export function initHashFocus() {
  window.addEventListener('hashchange', handleHashFocus);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleHashFocus, {
      once: true,
    });
  } else {
    handleHashFocus();
  }
}

// Auto-initialize when imported
initHashFocus();

export default initHashFocus;
